import { TaskCategoryTaskType, TaskStepDto } from '@urbanx/agx.activities.client'
import { AgxBadge, AgxCheckbox, AgxTypography } from '@urbanx/agx.ui-components2'
import clsx from 'clsx'
import { getTaskStepDueDate } from 'utils/tasks'
import TaskDueDateBadge from './TaskDueDateBadge'
import { useMemo } from 'react'

const settlementTaskTypes = new Set([
  TaskCategoryTaskType.Settlement,
  TaskCategoryTaskType.SettlementPayments,
  TaskCategoryTaskType.SettlementPaymentsReview,
  TaskCategoryTaskType.VendorBankDetailsSettlement,
])

interface TaskStepItemProps {
  step: TaskStepDto
  allStepsCompleted: boolean
  taskId?: string
  taskType?: TaskCategoryTaskType
  toggleTaskSteps: (payload: {
    taskId?: string
    stepIds: number[]
  }) => void
}

const TaskStepItem = ({
  step,
  allStepsCompleted,
  taskId,
  taskType,
  toggleTaskSteps,
}: TaskStepItemProps) => {
  const labelText = step.name ?? ''
  const helperText = step.data?.text
  const badgeMessage = step.data?.badgeMessage?.trim()
  const stepDueDate = useMemo(() => getTaskStepDueDate(step), [step])
  const showBadges = !taskType || !settlementTaskTypes.has(taskType)
  const hasHelperContent =
    Boolean(helperText) || (showBadges && Boolean(badgeMessage)) || (showBadges && Boolean(stepDueDate))

  const toggleStep = () =>
    toggleTaskSteps({
      taskId,
      stepIds: [step.order!],
    })

  const onCardClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (allStepsCompleted) return
    // The checkbox/label and any links handle their own clicks.
    if ((event.target as HTMLElement).closest('input, label, a, button')) return

    toggleStep()
  }

  return (
    <div
      className={clsx('taskStep-container', 'taskStep-item', {
        disabled: allStepsCompleted,
      })}
      onClick={onCardClick}
    >
      <AgxCheckbox
        id={`${step.order}-${step.name}`}
        checked={!!step.isCompleted}
        onChange={toggleStep}
        strikethroughOnCheck
        label={labelText}
        disabled={allStepsCompleted}
      />
      {hasHelperContent && (
        <div className="taskStep-helperContent">
          <AgxTypography
            id={`task-step-helper-${step.order}`}
            key={`task-step-helper-${step.order}`}
            type={'body_ui_strong'}
            size={'medium'}
            color="var(--text-primary)"
          >
            {helperText}
          </AgxTypography>
          {showBadges && stepDueDate && (
            <div className="taskStep-dueDateBadge">
              <TaskDueDateBadge
                dueDate={stepDueDate}
                isCompleted={step.isCompleted ?? false}
                completedDate={step.completedDate}
              />
            </div>
          )}
          {showBadges && badgeMessage && (
            <div className="taskStep-helperBadge">
              <AgxBadge
                type="colourfill"
                colour="yellow"
                size="md"
                text={badgeMessage}
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default TaskStepItem
